import * as THREE from "three"
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Experience from "./Experience.js"

export default class Camera {
    constructor() {
        this.experience = new Experience()
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.canvas = this.experience.canvas

        // Camera modes
        this.testingMode = false // Set to true for orbit controls
        
        // Third-person camera settings (can be updated via GameSettings)
        this.cameraSettings = {
            distance: 16,
            height: 6.2,
            smoothness: 0.11,
            fov: 72,
            lookAheadDistance: 11,
            speedFovBoost: 9
        }
        
        // Camera state
        this.targetPosition = new THREE.Vector3()
        this.currentLookAt = new THREE.Vector3()
        this.baseFov = this.cameraSettings.fov
        this.targetFov = this.baseFov
        this.drivingSpeed = 0
        this._speedClock = 0
        this.previousForward = new THREE.Vector3(0, 0, 1)
        this.cameraRoll = 0
        this.targetRoll = 0

        this.setInstance()

        if (this.testingMode) {
            this.setOrbitControls()
        }
    }

    setInstance() {
        this.instance = new THREE.PerspectiveCamera(
            this.cameraSettings.fov,
            this.sizes.width / this.sizes.height,
            0.1,
            1000
        )

        this.instance.position.set(5, 15, 25)
        this.instance.lookAt(new THREE.Vector3(5, 0, 0))
        this.scene.add(this.instance)
    }

    setOrbitControls() {
        this.controls = new OrbitControls(
            this.instance,
            this.canvas
        )

        this.controls.enableDamping = true
        this.controls.target.set(5, 5, 0)
    }
    
    // Update camera settings from GameSettings
    updateSettings(settings) {
        if (settings.distance !== undefined) this.cameraSettings.distance = settings.distance
        if (settings.height !== undefined) this.cameraSettings.height = settings.height
        if (settings.smoothness !== undefined) this.cameraSettings.smoothness = settings.smoothness
        if (settings.fov !== undefined) {
            this.baseFov = settings.fov
            this.cameraSettings.fov = settings.fov
            this.targetFov = settings.fov
        }
    }
    
    // Efeito de velocidade inspirado em jogos de corrida: o FOV abre gradualmente, sem saltos.
    setDrivingSpeed(speed) {
        this.drivingSpeed = Math.max(0, Number(speed) || 0)
        const intensity = THREE.MathUtils.clamp(this.drivingSpeed / 32, 0, 1)
        this.targetFov = this.baseFov + intensity * this.cameraSettings.speedFovBoost
    }

    // Third-person follow camera update
    followTarget(target, forward) {
        if (this.testingMode || !target) return
        
        const { distance, height, smoothness, lookAheadDistance } = this.cameraSettings
        
        // Calculate ideal camera position behind the target
        const offset = forward.clone().multiplyScalar(-distance)
        offset.y = height
        
        this.targetPosition.copy(target).add(offset)
        
        // A câmera responde um pouco mais rápido em alta velocidade, mas mantém suavidade.
        const speedBlend = THREE.MathUtils.clamp(this.drivingSpeed / 38, 0, 1)
        const followAlpha = THREE.MathUtils.clamp(smoothness + speedBlend * 0.035, 0.04, 0.22)
        this.instance.position.lerp(this.targetPosition, followAlpha)

        // Inclinação sutil em curvas: reforça a sensação de massa sem causar enjoo.
        const turnAmount = this.previousForward.x * forward.z - this.previousForward.z * forward.x
        this.targetRoll = THREE.MathUtils.clamp(-turnAmount * 1.35, -0.045, 0.045)
        this.cameraRoll += (this.targetRoll - this.cameraRoll) * 0.12
        this.previousForward.copy(forward)
        
        // FOV dinâmico dá sensação de aceleração sem exagerar em baixa velocidade.
        this.instance.fov += (this.targetFov - this.instance.fov) * 0.08
        this.instance.updateProjectionMatrix()

        // Look ahead of the car
        const lookAtPoint = target.clone()
        lookAtPoint.add(forward.clone().multiplyScalar(lookAheadDistance))
        lookAtPoint.y = target.y + 1
        
        // Smooth look-at
        this.currentLookAt.lerp(lookAtPoint, followAlpha * 1.5)
        this.instance.lookAt(this.currentLookAt)
        this.instance.rotateZ(this.cameraRoll)
    }

    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height
        this.instance.updateProjectionMatrix()
    }

    update() {
        this._speedClock += 0.016
        if (this.testingMode && this.controls) {
            this.controls.update()
        }
    }
}